import { History } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { StatusBadge } from "@/components/admin/XeroRunDisplay";
import { formatNumber, formatWhen } from "@/lib/xeroRunFormat";
import { useXeroOrgName } from "@/hooks/useXeroOrgName";
import { type XeroRun } from "@/services/xeroApi";


export default function XeroRecentRunsDrawer({ runs }: { runs: XeroRun[] }) {
  const orgName = useXeroOrgName();
  const empty = runs.length === 0;

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          disabled={empty}
          title={
            empty
              ? "No runs reported yet"
              : `Recent runs (last ${runs.length})`
          }
          aria-label={
            empty ? "No runs reported yet" : `Recent runs, last ${runs.length}`
          }
        >
          <History className="h-4 w-4" />
        </Button>
      </SheetTrigger>

      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 sm:max-w-lg"
      >
        <SheetHeader className="pr-8">
          <SheetTitle>Recent runs</SheetTitle>
          <SheetDescription>
            The last {runs.length} {runs.length === 1 ? "run" : "runs"} reported
            by the Fabric pipeline, newest first. One row per organisation per
            run.
          </SheetDescription>
        </SheetHeader>

        <div className="-mx-6 mt-4 flex-1 divide-y divide-border overflow-y-auto border-t border-border">
          {runs.map((run) => (
            <div key={`${run.run_id}-${run.tenant_id}`} className="px-6 py-3">
              <div className="flex items-start justify-between gap-3">
                <span className="text-sm font-medium text-foreground">
                  {orgName(run.tenant_id, run.tenant_name)}
                </span>
                <StatusBadge status={run.status} />
              </div>

              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                <span>{formatWhen(run.started_at)}</span>
                <span aria-hidden>·</span>
                <span>{formatNumber(run.rows_ingested)} rows</span>
                <span aria-hidden>·</span>
                <span>{formatNumber(run.pages_fetched)} pages</span>
              </div>

              {run.error_message && (
                <p className="mt-1.5 text-xs text-destructive">
                  {run.error_message}
                </p>
              )}
            </div>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}
